/**
 * Generic module-completion watcher.
 *
 * For every module that is currently flagged complete, runs
 * `validateModuleCompletion` (which dispatches to the extension handler's
 * `validateCompletionClient`) and, when the precheck now fails, flips the
 * completion flag back to incomplete on the server + locally.
 *
 * This is how we keep `moduleCompletions` in agreement with the live Zustand
 * stores: edits to masks/scores/vectors/texts/image-labels cause React to
 * re-run this hook (Zustand is the pub-sub bus), which consults the registered
 * extension handlers \u2014 no module-type-specific branching lives here.
 */

import {
  type CompletionCheckResult,
  type DispatcherProjectModule,
  type ValidateModuleCompletionInput,
  validateModuleCompletion
} from "@frontend/pages/case-workspace/lib/validate-module-completion"
import type {
  ImageLabelAnnotationState,
  LabelRasterAnnotationState,
  MaskAnnotationState,
  ModuleCompletionState,
  ScoreAnnotationState,
  TextAnnotationState,
  VectorAnnotationState
} from "@shared/lib/history-types"
import { useEffect, useRef } from "react"
import { toast } from "sonner"

export interface ModuleCompletionWatcherParams {
  modules: readonly DispatcherProjectModule[]
  moduleCompletions: readonly ModuleCompletionState[]
  masks?: readonly MaskAnnotationState[]
  scores?: readonly ScoreAnnotationState[]
  vectors?: readonly VectorAnnotationState[]
  texts?: readonly TextAnnotationState[]
  imageLabels?: readonly ImageLabelAnnotationState[]
  labelRasters?: readonly LabelRasterAnnotationState[]
  setModuleCompletionRemote: (moduleId: string, completed: boolean) => Promise<void>
  /** Skip the check entirely when the workspace is view-only / review-locked / still bootstrapping. */
  isReadOnly: boolean
}

/**
 * Pure decision helper exported for tests.
 *
 * Returns the set of currently-completed modules whose precheck now fails,
 * each with the first error string (for a human-readable toast).
 */
export function computeModulesToUncomplete(params: {
  modules: readonly DispatcherProjectModule[]
  moduleCompletions: readonly ModuleCompletionState[]
  evaluate: (module: DispatcherProjectModule) => CompletionCheckResult
  isReadOnly: boolean
}): Array<{ moduleId: string; moduleName: string; firstError: string }> {
  if (params.isReadOnly) return []

  const completedById = new Map<string, ModuleCompletionState>()
  for (const entry of params.moduleCompletions) {
    if (entry.completed) completedById.set(entry.projectModuleId, entry)
  }

  const out: Array<{ moduleId: string; moduleName: string; firstError: string }> = []
  for (const module of params.modules) {
    if (!completedById.has(module.id)) continue
    const result = params.evaluate(module)
    if (!result.valid && result.errors.length > 0) {
      out.push({
        moduleId: module.id,
        moduleName: module.template_name,
        firstError: result.errors[0]!
      })
    }
  }
  return out
}

export function useModuleCompletionWatcher(params: ModuleCompletionWatcherParams): void {
  const { modules, moduleCompletions, setModuleCompletionRemote, isReadOnly } = params

  // Track in-flight uncomplete calls so we don't spam the server with
  // duplicate requests before the Zustand state round-trips.
  const inFlightRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (isReadOnly) return

    const failures = computeModulesToUncomplete({
      modules,
      moduleCompletions,
      isReadOnly,
      evaluate: (module) =>
        validateModuleCompletion({
          module,
          modules,
          masks: params.masks,
          scores: params.scores,
          vectors: params.vectors,
          texts: params.texts,
          imageLabels: params.imageLabels,
          labelRasters: params.labelRasters
        } satisfies ValidateModuleCompletionInput)
    })

    for (const failure of failures) {
      if (inFlightRef.current.has(failure.moduleId)) continue
      inFlightRef.current.add(failure.moduleId)

      toast.info(`${failure.moduleName} needs attention`, {
        description: failure.firstError
      })

      setModuleCompletionRemote(failure.moduleId, false).finally(() => {
        inFlightRef.current.delete(failure.moduleId)
      })
    }
    // Intentionally include the store slices so the effect re-runs on any edit.
  }, [
    modules,
    moduleCompletions,
    params.masks,
    params.scores,
    params.vectors,
    params.texts,
    params.imageLabels,
    params.labelRasters,
    setModuleCompletionRemote,
    isReadOnly
  ])
}
